import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Edit, RotateCcw, Trash2 } from 'lucide-react';
import { cabinetSubscriptionsAPI } from '../../lib/api';
import {
  formatCoef,
  formatMoney,
  inferBuyerCurrency,
  moneyPrice,
  monthRange,
  nextChargeDateFromPeriod,
  normalizeDecimal,
  todayISO,
} from '../../lib/subscriptionBilling';
import { useToast } from '../../hooks/use-toast';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import CabinetCatalogPicker from './CabinetCatalogPicker';

const PERIOD_LABELS = { month: 'Раз в месяц', year: 'Раз в год', once: 'Разово' };

const emptyForm = (clientId = '') => ({
  charge_date: todayISO(),
  client_id: clientId,
  service_id: '',
  tariff_id: '',
  currency: 'usd',
  client_price: '',
  exchange_rate: '',
  rub_price: '',
  billing_period: 'month',
  next_charge_date: nextChargeDateFromPeriod(todayISO(), 'month'),
  notify_email: '',
});

function formatDate(iso) {
  if (!iso || !iso.includes('-')) return iso || '—';
  const [year, month, day] = iso.split('-');
  return `${day}.${month}.${year}`;
}

function formatDeletedAt(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const CabinetSubscriptions = () => {
  const { toast } = useToast();
  const initialRange = monthRange();
  const [charges, setCharges] = useState([]);
  const [trash, setTrash] = useState([]);
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [tariffs, setTariffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(initialRange.dateFrom);
  const [dateTo, setDateTo] = useState(initialRange.dateTo);
  const [companyId, setCompanyId] = useState('all');
  const [emailDrafts, setEmailDrafts] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(() => emptyForm());
  const [savingForm, setSavingForm] = useState(false);
  const [showTrash, setShowTrash] = useState(false);
  const [restoringId, setRestoringId] = useState(null);
  const [catalogOpen, setCatalogOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rows, clientRows, serviceRows, trashRows] = await Promise.all([
        cabinetSubscriptionsAPI.getCharges(),
        cabinetSubscriptionsAPI.getClients(),
        cabinetSubscriptionsAPI.getServices(),
        cabinetSubscriptionsAPI.getTrash(),
      ]);
      setCharges(rows || []);
      setClients(clientRows || []);
      setServices(serviceRows || []);
      setTrash(trashRows || []);
      const drafts = {};
      (rows || []).forEach((row) => {
        drafts[row.id] = row.notify_email || '';
      });
      setEmailDrafts(drafts);
      setForm((prev) => {
        if (prev.client_id || editingId) return prev;
        const only = (clientRows || []).length === 1 ? String(clientRows[0].id) : '';
        return { ...prev, client_id: only };
      });
    } catch (error) {
      toast({ title: 'Ошибка загрузки', description: error.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!form.service_id) {
      setTariffs([]);
      return;
    }
    let cancelled = false;
    cabinetSubscriptionsAPI.getTariffs(form.service_id)
      .then((rows) => {
        if (!cancelled) setTariffs(rows || []);
      })
      .catch((error) => {
        if (!cancelled) {
          toast({ title: 'Не удалось загрузить тарифы', description: error.message, variant: 'destructive' });
        }
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.service_id]);

  const companies = useMemo(() => {
    const map = new Map();
    charges.forEach((row) => {
      map.set(row.company_id, row.company_name);
    });
    clients.forEach((row) => {
      map.set(row.company_id, row.company_name);
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [charges, clients]);

  const rubPreview = useMemo(() => {
    if (form.currency === 'rub') return normalizeDecimal(form.rub_price) || '';
    return moneyPrice(form.client_price, form.exchange_rate || '1') || '';
  }, [form.currency, form.client_price, form.exchange_rate, form.rub_price]);

  const resetForm = () => {
    const only = clients.length === 1 ? String(clients[0].id) : '';
    setEditingId(null);
    setForm(emptyForm(only));
  };

  const applyCatalogSelection = async (resolved) => {
    const [serviceRows, tariffRows] = await Promise.all([
      cabinetSubscriptionsAPI.getServices(),
      cabinetSubscriptionsAPI.getTariffs(resolved.service_id),
    ]);
    setServices(serviceRows || []);
    setTariffs(tariffRows || []);
    setForm((prev) => {
      const currency = resolved.suggested_currency === 'rub' ? 'rub' : 'usd';
      return {
        ...prev,
        service_id: String(resolved.service_id),
        tariff_id: String(resolved.tariff_id),
        currency,
        client_price: currency === 'usd' ? (resolved.suggested_client_price || '') : '',
        exchange_rate: currency === 'usd' ? prev.exchange_rate : '',
        rub_price: currency === 'rub' ? (resolved.suggested_rub_price || '') : '',
      };
    });
    toast({ title: 'Сервис и тариф из каталога подставлены в форму' });
  };

  const startEdit = (row) => {
    const currency = inferBuyerCurrency(row.client_price, row.exchange_rate, row.rub_price);
    setEditingId(row.id);
    setForm({
      charge_date: row.charge_date || todayISO(),
      client_id: String(row.client_id || ''),
      service_id: String(row.service_id || ''),
      tariff_id: String(row.tariff_id || ''),
      currency,
      client_price: currency === 'usd' ? String(row.client_price || '') : '',
      exchange_rate: currency === 'usd' ? String(row.exchange_rate || '') : '',
      rub_price: currency === 'rub' ? String(row.rub_price || '') : '',
      billing_period: row.billing_period || 'month',
      next_charge_date: row.next_charge_date || '',
      notify_email: row.notify_email || '',
    });
  };

  const setPeriodFields = (patch) => {
    setForm((prev) => {
      const next = { ...prev, ...patch };
      const periodChanged = Object.prototype.hasOwnProperty.call(patch, 'billing_period')
        || Object.prototype.hasOwnProperty.call(patch, 'charge_date');
      if (periodChanged && next.billing_period === 'once') {
        next.next_charge_date = '';
      } else if (periodChanged && (next.billing_period === 'month' || next.billing_period === 'year')) {
        next.next_charge_date = nextChargeDateFromPeriod(next.charge_date, next.billing_period);
      }
      return next;
    });
  };

  const saveForm = async (event) => {
    event.preventDefault();
    if (!form.client_id || !form.service_id || !form.tariff_id) {
      toast({ title: 'Заполните клиента, сервис и тариф', variant: 'destructive' });
      return;
    }
    const payload = {
      charge_date: form.charge_date,
      client_id: Number(form.client_id),
      service_id: Number(form.service_id),
      tariff_id: Number(form.tariff_id),
      currency: form.currency,
      billing_period: form.billing_period || null,
      next_charge_date: form.billing_period === 'once' ? null : (form.next_charge_date || null),
      notify_email: form.notify_email || '',
    };
    if (form.currency === 'usd') {
      payload.client_price = normalizeDecimal(form.client_price);
      payload.exchange_rate = normalizeDecimal(form.exchange_rate);
    } else {
      payload.rub_price = normalizeDecimal(form.rub_price);
    }
    setSavingForm(true);
    try {
      if (editingId) {
        await cabinetSubscriptionsAPI.updateCharge(editingId, payload);
        toast({ title: 'Лицензия сохранена' });
      } else {
        await cabinetSubscriptionsAPI.createCharge(payload);
        toast({ title: 'Лицензия добавлена' });
      }
      resetForm();
      await load();
    } catch (error) {
      toast({ title: 'Не сохранилось', description: error.message, variant: 'destructive' });
    } finally {
      setSavingForm(false);
    }
  };

  const removeCharge = async (row) => {
    if (!window.confirm(`Переместить лицензию ${row.service_name} / ${row.tariff_name} в корзину?`)) return;
    try {
      await cabinetSubscriptionsAPI.deleteCharge(row.id);
      if (editingId === row.id) resetForm();
      toast({ title: 'Лицензия в корзине' });
      await load();
    } catch (error) {
      toast({ title: 'Не удалось удалить', description: error.message, variant: 'destructive' });
    }
  };

  const restoreCharge = async (row) => {
    setRestoringId(row.id);
    try {
      await cabinetSubscriptionsAPI.restoreCharge(row.id);
      toast({ title: 'Лицензия восстановлена' });
      await load();
    } catch (error) {
      toast({ title: 'Не удалось восстановить', description: error.message, variant: 'destructive' });
    } finally {
      setRestoringId(null);
    }
  };

  const saveEmail = async (row) => {
    setSavingId(row.id);
    try {
      const updated = await cabinetSubscriptionsAPI.updateNotifyEmail(row.id, emailDrafts[row.id] || '');
      setCharges((prev) => prev.map((item) => (item.id === row.id ? { ...item, ...updated } : item)));
      toast({ title: 'Email сохранён' });
    } catch (error) {
      toast({ title: 'Не сохранилось', description: error.message, variant: 'destructive' });
    } finally {
      setSavingId(null);
    }
  };

  const exportReport = async () => {
    if (!dateFrom || !dateTo) {
      toast({ title: 'Укажите период', variant: 'destructive' });
      return;
    }
    setExporting(true);
    try {
      const params = { date_from: dateFrom, date_to: dateTo };
      if (companyId !== 'all') params.company_id = companyId;
      await cabinetSubscriptionsAPI.export(params);
    } catch (error) {
      toast({ title: 'Экспорт не удался', description: error.message, variant: 'destructive' });
    } finally {
      setExporting(false);
    }
  };

  const filteredTariffs = tariffs.filter(
    (item) => String(item.service_id) === String(form.service_id)
  );

  return (
    <div className="space-y-4">
      <CabinetCatalogPicker
        open={catalogOpen}
        onOpenChange={setCatalogOpen}
        resolveCatalog={cabinetSubscriptionsAPI.resolveCatalog}
        onResolved={applyCatalogSelection}
      />
      <Card>
        <CardHeader>
          <CardTitle>Управление лицензиями</CardTitle>
          <CardDescription>
            Добавляйте и ведите лицензии сами или вместе с менеджером Антологии.
            Уведомления о продлении — за 2 недели, за 1 неделю и в день оплаты.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <form onSubmit={saveForm} className="space-y-3 rounded-md border p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-sm font-medium">
                {editingId ? 'Редактировать лицензию' : 'Добавить лицензию'}
              </h3>
              {editingId && (
                <Button type="button" variant="ghost" size="sm" onClick={resetForm}>
                  Отмена
                </Button>
              )}
            </div>
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              <div className="space-y-2">
                <Label>Дата</Label>
                <Input
                  type="date"
                  value={form.charge_date}
                  onChange={(event) => setPeriodFields({ charge_date: event.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Клиент</Label>
                <Select
                  value={form.client_id || '_none'}
                  onValueChange={(value) => setForm((prev) => ({
                    ...prev,
                    client_id: value === '_none' ? '' : value,
                  }))}
                  disabled={clients.length === 1}
                >
                  <SelectTrigger><SelectValue placeholder="Клиент" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">Выберите клиента</SelectItem>
                    {clients.map((item) => (
                      <SelectItem key={item.id} value={String(item.id)}>
                        {item.name} · {item.company_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 md:col-span-2 lg:col-span-3">
                <div className="flex flex-wrap items-end justify-between gap-2">
                  <div className="space-y-1">
                    <Label>Сервис и тариф</Label>
                    <p className="text-xs text-muted-foreground">
                      Выберите вручную ниже или из каталога решений.
                    </p>
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={() => setCatalogOpen(true)}>
                    Выбрать из каталога
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Сервис</Label>
                <Select
                  value={form.service_id || '_none'}
                  onValueChange={(value) => setForm((prev) => ({
                    ...prev,
                    service_id: value === '_none' ? '' : value,
                    tariff_id: '',
                  }))}
                >
                  <SelectTrigger><SelectValue placeholder="Сервис" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">Выберите сервис</SelectItem>
                    {services.map((item) => (
                      <SelectItem key={item.id} value={String(item.id)}>{item.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Тариф</Label>
                <Select
                  value={form.tariff_id || '_none'}
                  onValueChange={(value) => {
                    if (value === '_none') {
                      setForm((prev) => ({ ...prev, tariff_id: '' }));
                      return;
                    }
                    const tariff = filteredTariffs.find((item) => String(item.id) === String(value));
                    setForm((prev) => ({
                      ...prev,
                      tariff_id: value,
                      client_price: prev.currency === 'usd' && tariff?.default_charge_price
                        ? String(tariff.default_charge_price)
                        : prev.client_price,
                    }));
                  }}
                  disabled={!form.service_id}
                >
                  <SelectTrigger><SelectValue placeholder="Тариф" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">Выберите тариф</SelectItem>
                    {filteredTariffs.map((item) => (
                      <SelectItem key={item.id} value={String(item.id)}>{item.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Валюта</Label>
                <Select
                  value={form.currency}
                  onValueChange={(value) => setForm((prev) => ({
                    ...prev,
                    currency: value,
                    client_price: value === 'usd' ? prev.client_price : '',
                    exchange_rate: value === 'usd' ? prev.exchange_rate : '',
                    rub_price: value === 'rub' ? prev.rub_price : '',
                  }))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="usd">Доллары (USD)</SelectItem>
                    <SelectItem value="rub">Рубли (RUB)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.currency === 'usd' ? (
                <>
                  <div className="space-y-2">
                    <Label>Цена в $</Label>
                    <Input
                      value={form.client_price}
                      onChange={(event) => setForm((prev) => ({ ...prev, client_price: event.target.value }))}
                      placeholder="100"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Курс</Label>
                    <Input
                      value={form.exchange_rate}
                      onChange={(event) => setForm((prev) => ({ ...prev, exchange_rate: event.target.value }))}
                      placeholder="92.5"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Цена в рублях</Label>
                    <Input value={rubPreview ? formatMoney(rubPreview) : ''} disabled readOnly />
                  </div>
                </>
              ) : (
                <div className="space-y-2">
                  <Label>Цена в рублях</Label>
                  <Input
                    value={form.rub_price}
                    onChange={(event) => setForm((prev) => ({ ...prev, rub_price: event.target.value }))}
                    placeholder="1500"
                    required
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label>Период</Label>
                <Select
                  value={form.billing_period || 'month'}
                  onValueChange={(value) => setPeriodFields({ billing_period: value })}
                >
                  <SelectTrigger><SelectValue placeholder="Период" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="month">Раз в месяц</SelectItem>
                    <SelectItem value="year">Раз в год</SelectItem>
                    <SelectItem value="once">Разово</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>След. списание</Label>
                <Input
                  type="date"
                  value={form.next_charge_date}
                  onChange={(event) => setForm((prev) => ({ ...prev, next_charge_date: event.target.value }))}
                  disabled={form.billing_period === 'once'}
                />
              </div>
              <div className="space-y-2">
                <Label>Email для уведомлений</Label>
                <Input
                  type="email"
                  value={form.notify_email}
                  onChange={(event) => setForm((prev) => ({ ...prev, notify_email: event.target.value }))}
                  placeholder="email@company.ru"
                  required
                />
              </div>
            </div>
            <Button type="submit" disabled={savingForm || clients.length === 0}>
              {savingForm ? 'Сохранение...' : (editingId ? 'Сохранить' : 'Добавить')}
            </Button>
            {clients.length === 0 && !loading && (
              <p className="text-sm text-muted-foreground">
                Нет привязанного клиента. Укажите ИНН в профиле или попросите менеджера привязать кабинет.
              </p>
            )}
          </form>

          <div className="grid gap-3 md:grid-cols-4">
            <div className="space-y-2">
              <Label>Период с</Label>
              <Input type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Период по</Label>
              <Input type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Компания</Label>
              <Select value={companyId} onValueChange={setCompanyId}>
                <SelectTrigger><SelectValue placeholder="Компания" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Авто / одна компания</SelectItem>
                  {companies.map((item) => (
                    <SelectItem key={item.id} value={String(item.id)}>{item.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-wrap items-end gap-2">
              <Button type="button" variant="outline" onClick={exportReport} disabled={exporting}>
                {exporting ? 'Выгрузка...' : 'Выгрузить Excel'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowTrash((prev) => !prev)}>
                Корзина{trash.length ? ` (${trash.length})` : ''}
              </Button>
            </div>
          </div>

          {showTrash && (
            <div className="space-y-2 rounded-md border p-4">
              <h3 className="text-sm font-medium">Корзина</h3>
              {trash.length === 0 ? (
                <p className="text-sm text-muted-foreground">Корзина пуста</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Запись</TableHead>
                      <TableHead>Удалено</TableHead>
                      <TableHead className="w-[140px]" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {trash.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>{row.title}</TableCell>
                        <TableCell className="whitespace-nowrap text-muted-foreground">
                          {formatDeletedAt(row.deleted_at)}
                        </TableCell>
                        <TableCell>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={restoringId === row.id}
                            onClick={() => restoreCharge(row)}
                          >
                            <RotateCcw className="mr-1 h-3.5 w-3.5" />
                            Восстановить
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          )}

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Дата</TableHead>
                  <TableHead>Клиент</TableHead>
                  <TableHead>Сервис</TableHead>
                  <TableHead>Тариф</TableHead>
                  <TableHead>Цена клиента</TableHead>
                  <TableHead>Курс</TableHead>
                  <TableHead>Цена в рублях</TableHead>
                  <TableHead>Период</TableHead>
                  <TableHead>След. списание</TableHead>
                  <TableHead>Статус</TableHead>
                  <TableHead>Email уведомлений</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (
                  <TableRow><TableCell colSpan={12}>Загрузка...</TableCell></TableRow>
                )}
                {!loading && charges.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={12}>
                      Пока нет лицензий. Добавьте запись выше или попросите менеджера привязать кабинет.
                    </TableCell>
                  </TableRow>
                )}
                {!loading && charges.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{formatDate(row.charge_date)}</TableCell>
                    <TableCell>{row.client_name}</TableCell>
                    <TableCell>{row.service_name}</TableCell>
                    <TableCell>{row.tariff_name}</TableCell>
                    <TableCell>{formatMoney(row.client_price)}</TableCell>
                    <TableCell>{formatCoef(row.exchange_rate)}</TableCell>
                    <TableCell>{formatMoney(row.rub_price)}</TableCell>
                    <TableCell>{PERIOD_LABELS[row.billing_period] || '—'}</TableCell>
                    <TableCell>{formatDate(row.next_charge_date)}</TableCell>
                    <TableCell>
                      {row.is_active
                        ? <Badge>активна</Badge>
                        : <Badge variant="secondary">неактивна</Badge>}
                    </TableCell>
                    <TableCell>
                      <div className="flex min-w-[220px] gap-2">
                        <Input
                          type="email"
                          value={emailDrafts[row.id] ?? ''}
                          onChange={(event) => setEmailDrafts((prev) => ({
                            ...prev,
                            [row.id]: event.target.value,
                          }))}
                          placeholder="email@company.ru"
                        />
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={savingId === row.id}
                          onClick={() => saveEmail(row)}
                        >
                          OK
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          onClick={() => startEdit(row)}
                          aria-label="Изменить"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          onClick={() => removeCharge(row)}
                          aria-label="Удалить"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CabinetSubscriptions;
